import type { HelpContent } from './types';

export const settingsUserPreferencesHelp: HelpContent = {
  pageTitle: 'User Preferences',
  description: 'User Preferences lets you personalise how the application looks and behaves for your account — changes here apply only to you and do not affect other users.',

  functionality: {
    overview:
      'This page contains personal display and navigation preferences. Unlike Settings, which apply company-wide, these options are per-user and can be changed by any user for their own account.',
    keyFeatures: [
      {
        label: 'Default landing page',
        detail: 'Choose which page opens when you log in or navigate to the root URL.',
      },
      {
        label: 'Remember last page',
        detail: 'When enabled, the application reopens on the last page you visited instead of the default landing page.',
      },
      {
        label: 'Welcome dialog',
        detail: 'Toggle whether the welcome dialog is shown when you first log in each session.',
      },
      {
        label: 'Keyboard shortcuts',
        detail: 'Enable or disable global keyboard shortcuts across the application.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Default Landing Page',
        description: 'A dropdown to choose which page you are taken to when you open the application.',
      },
      {
        name: 'Remember Last Page',
        description: 'A toggle that, when on, overrides the default landing page and returns you to the last page you visited.',
      },
      {
        name: 'Show Welcome Dialog',
        description: 'A toggle to show or hide the welcome dialog at the start of each session.',
      },
      {
        name: 'Keyboard Shortcuts',
        description: 'A toggle to enable or disable global keyboard shortcuts (e.g. Ctrl/Cmd + K to open the command palette).',
      },
    ],
    useCases: [
      'A project manager sets their default landing page to Projects so they always start on their project list.',
      'A user turns off the welcome dialog after their first week because they no longer need the onboarding prompts.',
      'A user who prefers mouse navigation disables keyboard shortcuts to avoid accidental activations.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Change any preference using the toggle, switch, or dropdown provided. Click Save to apply your changes.',
    steps: [
      {
        title: 'How do I change my default landing page?',
        items: [
          { text: 'Select a page from the Default Landing Page dropdown.' },
          {
            text: 'Make sure Remember Last Page is turned off, otherwise it will override your selection.',
            note: 'If Remember Last Page is on, the application always opens on the last page you visited, ignoring the Default Landing Page setting.',
          },
          { text: 'Click Save.' },
        ],
      },
      {
        title: 'How do I turn off keyboard shortcuts?',
        items: [
          { text: 'Find the Keyboard Shortcuts toggle.' },
          { text: 'Switch it to Off.' },
          { text: 'Click Save.' },
          {
            text: 'All global keyboard shortcuts (including Ctrl/Cmd + K, Ctrl/Cmd + /, and navigation shortcuts) are disabled until you turn them back on.',
            tip: 'If you want to see the list of available shortcuts before disabling them, press Ctrl/Cmd + / first.',
          },
        ],
      },
    ],
    proTips: [
      'Set Remember Last Page on if you work across multiple pages throughout the day — it saves you navigating back to where you were after each login.',
      'If you share a workstation with another user, set a specific default landing page so your session starts predictably regardless of what the previous user was doing.',
    ],
  },

  faq: [
    {
      question: 'Do my user preferences affect other users?',
      answer: 'No. User Preferences apply only to your own account. Other users have their own independent preferences.',
    },
    {
      question: 'What is the difference between User Preferences and Settings?',
      answer:
        'Settings (accessible from the main Settings hub) apply company-wide to all users. User Preferences apply only to your own account — they control personal display and navigation behaviour.',
    },
    {
      question: 'Why is my landing page not working as expected?',
      answer:
        'Check that the Remember Last Page toggle is turned off. If it is on, the application ignores the Default Landing Page and opens the last page you visited instead.',
    },
    {
      question: 'Can I re-enable the welcome dialog after I turned it off?',
      answer: 'Yes. Return to User Preferences, turn the Show Welcome Dialog toggle back on, and click Save.',
    },
  ],

  relatedPages: [
    { label: 'Settings Hub', path: '/settings' },
    { label: 'General Settings', path: '/settings/general' },
  ],
};
